import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowLeft, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/auth-context";
import { fetchWithAuth } from "@/lib/auth-client";
import type { CreateVendorRequest, VendorDto } from "@procureflow/shared-types";

export const Route = createFileRoute("/vendors/new")({
  head: () => ({
    meta: [
      { title: "Create Vendor — ProcureFlow" },
      { name: "description", content: "Register a new vendor record." },
    ],
  }),
  component: VendorNew,
});

interface FormState extends CreateVendorRequest {}

const EMPTY_FORM: FormState = {
  company_name:   "",
  address:        "",
  contact_name:   "",
  contact_email:  "",
  contact_phone:  "",
  bank_details:   "",
  tax_id:         "",
};

const FIELDS: Array<{ key: keyof FormState; label: string; placeholder?: string; type?: string }> = [
  { key: "company_name",  label: "Company Name",  placeholder: "Apex Office Supplies Ltd" },
  { key: "address",       label: "Address",       placeholder: "Street, City, Postal Code, Country" },
  { key: "contact_name",  label: "Contact Name",  placeholder: "Jane Doe" },
  { key: "contact_email", label: "Contact Email", placeholder: "vendor@example.com", type: "email" },
  { key: "contact_phone", label: "Contact Phone", placeholder: "+44 161 555 1212" },
  { key: "bank_details",  label: "Bank Details",  placeholder: "IBAN GB29NWBK60161331926819" },
  { key: "tax_id",        label: "Tax ID",        placeholder: "GB123456789" },
];

function VendorNew() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const canCreate = user?.role === "ProcurementOfficer" || user?.role === "Admin";

  if (!canCreate) {
    return (
      <div className="rounded-2xl bg-card border border-border shadow-elegant p-10 text-center space-y-2">
        <p className="text-sm font-medium text-foreground">Access restricted</p>
        <p className="text-sm text-muted-foreground">
          Vendor creation is available to Procurement Officers and Admins.
        </p>
      </div>
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    // Required-field validation mirrors the MSW + BE handler contract.
    const missing = FIELDS.filter(f => !form[f.key].trim()).map(f => f.label);
    if (missing.length > 0) {
      setFormError(`Missing required fields: ${missing.join(", ")}.`);
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetchWithAuth("/api/vendors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setFormError(body.message ?? "Failed to create vendor.");
        return;
      }
      const { data } = (await res.json()) as { data: VendorDto };
      navigate({ to: "/vendors/$id", params: { id: data.id }, replace: true });
    } catch {
      setFormError("An unexpected error occurred.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-2xl space-y-5">
      <div>
        <button
          onClick={() => navigate({ to: "/vendors" })}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" /> Back to vendors
        </button>
        <h1 className="text-lg font-semibold tracking-tight mt-2">Create Vendor</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          New vendors start in <strong>Pending Approval</strong> status and require Admin approval before becoming Active.
        </p>
      </div>

      {formError && (
        <div className="flex items-start gap-2.5 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          {formError}
        </div>
      )}

      <form onSubmit={handleSubmit} className="rounded-2xl bg-card border border-border shadow-elegant p-6 space-y-4" noValidate>
        {FIELDS.map(field => (
          <label key={field.key} className="block space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">{field.label}</span>
            <input
              type={field.type ?? "text"}
              value={form[field.key]}
              onChange={e => setForm(s => ({ ...s, [field.key]: e.target.value }))}
              placeholder={field.placeholder}
              aria-label={field.label}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
          </label>
        ))}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={() => navigate({ to: "/vendors" })}
            className="rounded-lg border border-border px-4 py-2 text-sm hover:bg-muted/40"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-glow hover:opacity-95 disabled:opacity-60"
          >
            {submitting ? "Saving…" : "Create Vendor"}
          </button>
        </div>
      </form>
    </div>
  );
}
